import React, { Component } from "react";
import {
	login,
	logout
} from '../../../../redux/features/auth/authSlice'
import AuthContainer from "../../AuthContainer";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import withRouter from "helpers/withRouter";
import { isEmail } from "utils/validation";
import { getPostLoginRedirectUrl, isRedirectUrlSameProduct } from "utils/environment";
import {
	Button,
	Checkbox,
	FormLayout,
	TextField,
	Text,
	Box,
} from "@shopify/polaris";

import passwordIMG from "img/icons/entities/password.svg";
import emailIMG from "img/icons/entities/email.svg";
import eyeOnIMG from "img/icons/misc/eye-on.svg";
import eyeOffIMG from "img/icons/misc/eye-off.svg";
import PageHelmet from "components/PageHelmet";
import { createCustomDispatch } from "helpers/customDispatch";
import LoginButtons from './LoginButtons'

class LogIn extends Component {
	constructor(props) {
		super(props);

		this.state = {
			step: "credentials",
			passwordChecked: false,
			email: "",
			errorEmail: false,
			password: "",
			errorPassword: false,
			showPassword: false,
			twoFaCode: "",
			twoFaError: "",
		};
	}

	handleChangeCheckedRememberPassword = (value) => {
		this.setState({passwordChecked: value});
	};

	togglePasswordVisibility = () => {
		this.setState(prevState => ({
			showPassword: !prevState.showPassword
		}));
	};

	validateEmail = (email) => {
		const {t} = this.props;
		if (email.length > 0 && !isEmail(email)) {
			this.setState({
				errorEmail: t("login.invalidEmail"),
			});
		} else {
			this.setState({
				errorEmail: false,
			});
		}
	};

	doLogin = async () => {
		const {t} = this.props;
		const {
			email,
			password
		} = this.state;

		if (!password) {
			this.setState({
				errorPassword: t("login.enterPassAndEmail"),
			});
			return;
		}

		const result = await this.props.dispatch(login({ email, password }));
		
		if (result.meta.requestStatus === 'rejected') {
			const errorMsg = 
				typeof result.payload === "string"
					? result.payload
					: result.payload?.error
						? result.payload.error
						: t("auth.login.emailOrPasswordIncorrect");
			this.setState({errorPassword: errorMsg});
			return;
		}

		const payload = result.payload;
		if (payload && payload.require_two_factor === true) {
			this.setState({
				step: "twoFactor",
				twoFaCode: "",
				twoFaError: "",
			});
			return;
		}

		this.handleLoginSuccess(payload);
	};

	submitTwoFactor = async () => {
		const {t} = this.props;
		const { email, password, twoFaCode } = this.state;
		
		if (!twoFaCode || twoFaCode.length !== 6) {
			return;
		}

		this.setState({ twoFaError: "" });

		const result = await this.props.dispatch(login({ email, password, code: twoFaCode }));
		
		if (result.meta.requestStatus === 'rejected') {
			const msg =
				typeof result.payload === "string"
					? result.payload
					: result.payload?.error
						? result.payload.error
						: t("auth.twoFa.wrongVerificationCode");
			this.setState({ twoFaError: msg });
			return;
		}

		const payload = result.payload;
		if (payload && payload.error) {
			this.setState({ twoFaError: payload.error });
			return;
		}

		this.handleLoginSuccess(payload);
	};

	handleLoginSuccess = (res) => {
		const params = new URLSearchParams(window.location.search);
		const redirectUrl = params.get('redirectUrl');
		const isLocalhost =
			window.location.hostname === "localhost" ||
			window.location.hostname === "127.0.0.1" ||
			window.location.hostname === "[::1]" ||
			/\.local$/.test(window.location.hostname);
		let subdomain = '';
		switch (res.app_id) {
			case 1: subdomain = 'tax'; break;
			case 2: subdomain = 'epr'; break;
			case 3: subdomain = 'invoice'; break;
			case 4: subdomain = 'tax-agent'; break;
			case 5: subdomain = 'omp'; break;
			default: subdomain = 'tax';
		}
		if (redirectUrl && isRedirectUrlSameProduct(redirectUrl)) {
			window.location.href = redirectUrl;
		} else if (!isLocalhost) {
			const postLoginUrl = getPostLoginRedirectUrl(subdomain);
			window.location.href = postLoginUrl || `https://${subdomain}${process.env.REACT_APP_COOKIES || ''}/`;
		} else {
			this.props.navigate("/home");
		}
	};

	handleBackToCredentials = () => {
		this.setState({
			step: "credentials",
			twoFaCode: "",
			twoFaError: "",
		});
	};

	handleTwoFaCodeChange = (value) => {
		const digitsOnly = value.replace(/\D/g, "").slice(0, 6);
		this.setState({
			twoFaCode: digitsOnly,
			twoFaError: "",
		});
	};

	render() {
		const {
			t,
			loggingIn
		} = this.props;
		const {
			step,
			passwordChecked,
			email,
			errorEmail,
			password,
			errorPassword,
			showPassword,
			twoFaCode,
			twoFaError
		} = this.state;

		// Check if running on dev or localhost (for manager redirect)
		const isDevOrLocalhost = (() => {
			const host = window.location.hostname;
			const isLocal = host === "localhost" || host === "127.0.0.1" || host === "[::1]" || /\.local$/.test(host);
			const isDev = (process.env.REACT_APP_API_URL || "").includes("dev");
			return isLocal || isDev;
		})();

		if (step === "twoFactor") {
			return (
				<>
					<AuthContainer
						title={t("auth.twoFa.title")}
						onParrotClick={isDevOrLocalhost ? () => this.props.navigate("/manager") : undefined}
						content={
							<form autoComplete='off' onSubmit={(e) => { e.preventDefault(); this.submitTwoFactor(); }}>
								<PageHelmet title={t("auth.twoFa.pageTitle")} />

								<FormLayout>
									<TextField
										name='code'
										type='text'
										label={
											<Text
												color='subdued'
												variant='bodySm'
											>
												{t("auth.twoFa.codeLabel")}
											</Text>
										}
										placeholder={t("auth.twoFa.codePlaceholder")}
										autoComplete='one-time-code'
										inputMode='numeric'
										value={twoFaCode}
										error={twoFaError}
										maxLength={6}
										onChange={this.handleTwoFaCodeChange}
									/>
								</FormLayout>
							</form>
						}
						secondaryContent={
							<div
								style={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'center',
									justifyContent: 'flex-end'
								}}
							>
								<h3 style={{marginRight: 10}}>
									{t("auth.login.dontHaveAccount")}
								</h3>
								<div style={{width: '100px'}}>
									<Button
										fullWidth
										variant='secondary'
										onClick={() => this.props.navigate("/signup")}
									>
										{t("auth.login.signUp")}
									</Button>
								</div>
							</div>
						}
						actions={
							<>
								<div
									style={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										marginTop: '95px'
									}}
								>
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										this.handleBackToCredentials();
									}}
									style={{
										color: '#267FD7',
										textDecoration: 'none',
										fontSize: '14px',
										fontWeight: 500,
									}}
									onMouseEnter={(e) => {
										e.target.style.textDecoration = 'underline';
									}}
									onMouseLeave={(e) => {
										e.target.style.textDecoration = 'none';
									}}
								>
									{t("auth.twoFa.backToSignIn")}
								</a>
								<Button
									disabled={loggingIn || twoFaCode.length !== 6}
									loading={loggingIn}
									variant='primary'
									onClick={this.submitTwoFactor}
								>
									{t("auth.twoFa.continue")}
								</Button>
							</div>
							<div style={{ height: '140px' }}></div>
							</>
						}
					/>
				</>
			);
		}

		return (
			<>
				<AuthContainer
					title={t("auth.login.welcomeBack")}
					onParrotClick={isDevOrLocalhost ? () => this.props.navigate("/manager") : undefined}
					content={
						<form autoComplete='off'>

							<PageHelmet title={t("auth.login.pageTitle")} />


							<FormLayout >
								<TextField
										name='email'
										type='email'
										label={
											<Text
													color='subdued'
													variant='bodySm'
											>
												{t("login.email")}
											</Text >
										}
										placeholder={t("login.email")}
										autoComplete='email'
										value={email}
										prefix={
											<img
													src={emailIMG}
													alt='email'
													style={{marginTop: 6}}
											/>
										}
										error={errorEmail}
										onBlur={() => this.validateEmail(email)}
										onChange={(value) => {
											this.setState({email: value});
										}}
								/>
								<TextField
										name='password'
										type={showPassword ? 'text' : 'password'}
										label={
											<Text
													color='subdued'
													variant='bodySm'
											>
												{t("login.password")}
											</Text >
										}
										placeholder={t("login.password")}
										value={password}
										prefix={
											<img
													src={passwordIMG}
													alt='password'
													style={{marginTop: 6}}
											/>
										}
										suffix={
											<img
													src={showPassword ? eyeOffIMG : eyeOnIMG}
													alt={showPassword ? t("auth.login.hidePassword") : t("auth.login.showPassword")}
													style={{
														marginTop: 6,
														cursor: 'pointer',
														width: '20px',
														height: '20px'
													}}
													onClick={this.togglePasswordVisibility}
											/>
										}
										error={errorPassword}
										onChange={(value) => {
											this.setState({
												password: value,
												errorPassword: false,
											});
										}}
								/>
								<Link
										className='Polaris-Link'
										to='/forgot'
								>
									{t("login.forgotPass")}
								</Link >
							</FormLayout >
						</form >
					}
					secondaryContent={
						<div
								style={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'center',
									justifyContent: 'flex-end'
								}}
						>
							<h3 style={{marginRight: 10}}>
								{t("auth.login.dontHaveAccount")}
							</h3 >
							<div style={{width: '100px'}}>
								<Button
										fullWidth
										variant='secondary'
										onClick={() => this.props.navigate("/signup")}
								>
									{t("auth.login.signUp")}
								</Button >
							</div >
						</div >
					}
					actions={
						<>
							<div
									style={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center'
									}}
							>
								<Checkbox
										checked={passwordChecked}
										label={t("login.rememberMe")}
										onChange={this.handleChangeCheckedRememberPassword}
								/>
								<Button
										disabled={loggingIn}
										loading={loggingIn}
										variant='primary'
										onClick={this.doLogin}
								>
									{t("login.signIn")}
								</Button >
							</div >
							<LoginButtons />
						</>
					}
				/>
			</>

		);
	}
}

const mapStateToProps = (state) => ({
	loginError: state.auth.loginError,
	loggingIn: state.auth.loggingInLoading,
});

const mapDispatchToProps = (defaultDispatch) => {
	const dispatch = createCustomDispatch(defaultDispatch);

	return {
		dispatch: defaultDispatch,
		login: (
				email,
				password
		) => dispatch(login({
			email,
			password
		})),
		logout: () => dispatch(logout()),
	};
};

export default withTranslation()(
		connect(mapStateToProps, mapDispatchToProps)(withRouter(LogIn))
);
